import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FitTable from './FitTable';

const API = process.env.REACT_APP_API_URL;

// Mirrors services/scorecardAgent.js SETTINGS_DEFAULTS.
export interface ScorecardAgentSettings {
  model: string;
  read_effort: string;
  chat_effort: string;
  max_tokens: number;
  structured_output: boolean;
  read_format: string;
  read_speed: string;
  read_passes: number;
  refusal_fallbacks: boolean;
  confidence_threshold: number;
  admin_email: string;
  escalate_after_hours: number;
  whatsapp_notify: boolean;
  max_agent_steps: number;
  read_timeout_seconds: number;
  per_request_seconds: number;
  notation_guide: string;
}

interface Suggestion { id: string; text: string; upload_id: number | null; at: string }

interface SettingsInfo {
  settings: ScorecardAgentSettings;
  defaults: ScorecardAgentSettings;
  limits: Record<string, { min: number; max: number }>;
  effort_levels: string[];
  read_formats?: string[];
  read_speeds?: string[];
  models: { id: string; label: string; note?: string }[];
  overridden: string[];
  updated_at: string | null;
  suggestions: Suggestion[];
}

interface UsagePart { requests?: number; input_tokens?: number; output_tokens?: number; cache_read_input_tokens?: number; turns?: number }
interface UsageRow {
  upload_id: number;
  course_name: string | null;
  round_date: string | null;
  status: string;
  model: string | null;
  usage: { read?: UsagePart; chat?: UsagePart };
  stats: { cells: number; kept: number; fixed_by_total: number; edited: number } | null;
  error: string | null;
  read_ms?: number | null;
  read_summary?: string | null;
  // Where a finished read's time went (services/agentKit.js timingOf).
  read_timing?: { thinking_ms: number; writing_ms: number; output_tokens: number | null; tokens_per_s: number | null; paused_ms?: number } | null;
  read_setup?: { model: string; effort: string | null; format: string; speed: string; passes?: number } | null;
  created_at: string;
}
interface UsageInfo {
  uploads: UsageRow[];
  totals: { uploads: number; committed: number; cells: number; kept: number; fixed_by_total: number; edited: number; input_tokens: number; output_tokens: number; cache_read_input_tokens: number };
}

const NUMERIC: { key: keyof ScorecardAgentSettings; label: string; hint: string }[] = [
  { key: 'escalate_after_hours', label: 'Hand to the admin after (hours)', hint: 'A review nobody touched this long goes to the admin too.' },
  { key: 'max_tokens', label: 'Max output tokens / request', hint: 'Room for the read (all rows × 18 holes) and reasoning.' },
  { key: 'max_agent_steps', label: 'Assistant tool steps / message', hint: 'Draft edits the assistant may make per reply.' },
  { key: 'read_timeout_seconds', label: 'Read time cap (s)', hint: 'Longest reading one card may take before it is stopped.' },
  { key: 'per_request_seconds', label: 'Chat reply time cap (s)', hint: 'Longest one assistant reply may take.' },
];

const FORMAT_LABELS: Record<string, string> = {
  full: 'Full — every detail for every cell',
  compact: 'Compact — the marks + only unsure cells',
};
const SPEED_LABELS: Record<string, string> = {
  standard: 'Standard',
  fast: 'Fast mode — up to 2.5× faster, 2× price',
};

const fmt = (n?: number | null) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—');
const mmss = (ms?: number | null) => {
  if (ms == null) return '—';
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

async function call<T>(getToken: () => Promise<string>, path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API}${path}`, { ...init, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(init.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Request failed (${res.status}).`);
  return body as T;
}

/**
 * Configuration → Scorecard Agent: the model and effort used to read
 * scorecard photos and to run the review chat, how unsure a reading may be
 * before a person must look, who the admin is, the house notation guide (and
 * the notes the assistant suggested for it), and what reads have cost / how
 * much of each read survived review.
 */
const ScorecardAgentCard: React.FC<{ getToken: () => Promise<string> }> = ({ getToken }) => {
  const [info, setInfo] = useState<SettingsInfo | null>(null);
  const [draft, setDraft] = useState<ScorecardAgentSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState<UsageInfo | null>(null);

  const apply = (i: SettingsInfo) => { setInfo(i); setDraft({ ...i.settings }); };

  const load = useCallback(async () => {
    try {
      apply(await call<SettingsInfo>(getToken, '/scorecard-agent/config'));
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Could not load the scorecard agent settings. Is the backend up to date (migrations applied)?');
    }
    try { setUsage(await call<UsageInfo>(getToken, '/scorecard-agent/usage?limit=10')); } catch { /* optional */ }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => !!info && !!draft && JSON.stringify(draft) !== JSON.stringify(info.settings), [info, draft]);
  const set = <K extends keyof ScorecardAgentSettings>(k: K, v: ScorecardAgentSettings[K]) => { setDraft((d) => (d ? { ...d, [k]: v } : d)); setMsg(null); };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setMsg(null);
    try { await fn(); } catch (err: any) { setMsg({ ok: false, text: err?.message || 'Something went wrong.' }); } finally { setBusy(false); }
  };

  const save = () => run(async () => {
    apply(await call<SettingsInfo>(getToken, '/scorecard-agent/config', { method: 'PUT', body: JSON.stringify({ settings: draft }) }));
    setMsg({ ok: true, text: 'Saved — applies to the next read or reply.' });
  });
  const reset = () => {
    if (!window.confirm('Reset every scorecard agent setting (including the notation guide) to its default?')) return;
    run(async () => { apply(await call<SettingsInfo>(getToken, '/scorecard-agent/config', { method: 'DELETE' })); setMsg({ ok: true, text: 'Back to defaults.' }); });
  };
  const resolve = (id: string, accept: boolean) => run(async () => {
    apply(await call<SettingsInfo>(getToken, `/scorecard-agent/suggestions/${id}`, { method: 'POST', body: JSON.stringify({ accept }) }));
    setMsg({ ok: true, text: accept ? 'Added to the notation guide.' : 'Suggestion dismissed.' });
  });

  if (error) {
    return <div className="bga-card"><h2 className="bga-card-title">Scorecard Agent</h2><p className="bga-muted" style={{ margin: 0 }}>{error}</p></div>;
  }
  if (!info || !draft) {
    return <div className="bga-card"><h2 className="bga-card-title">Scorecard Agent</h2><p className="bga-muted" style={{ margin: 0 }}>Loading…</p></div>;
  }

  const t = usage?.totals;
  const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
  const hint: React.CSSProperties = { fontSize: 11.5, color: 'var(--bga-muted)', lineHeight: 1.35 };
  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '14px 20px' };
  const section: React.CSSProperties = { fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--bga-muted)', margin: '22px 0 10px' };

  return (
    <div className="bga-card">
      <h2 className="bga-card-title" style={{ marginBottom: 4 }}>
        Scorecard Agent
        <span className={`bga-badge ${info.overridden.length ? 'bga-badge-amber' : 'bga-badge-gray'}`} style={{ marginLeft: 10, verticalAlign: 'middle' }}>
          {info.overridden.length ? `${info.overridden.length} override${info.overridden.length === 1 ? '' : 's'}` : 'defaults'}
        </span>
      </h2>
      <div className="bga-muted" style={{ fontSize: 13 }}>
        Reads photos of paper scorecards into hole-by-hole scores (Rounds → 📷 Scorecard) and runs the review chat.
        Anything it's unsure of goes to the uploader — or the admin below — before it is saved.
      </div>

      <div style={section}>Model &amp; reasoning</div>
      <div style={grid}>
        <div className="bga-field" style={field}>
          <label className="bga-label" htmlFor="sa-model">Model</label>
          <select id="sa-model" className="bga-select" value={draft.model} onChange={(e) => set('model', e.target.value)}>
            {info.models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            {!info.models.some((m) => m.id === draft.model) && <option value={draft.model}>{draft.model}</option>}
          </select>
          <span style={hint}>{info.models.find((m) => m.id === draft.model)?.note || ''}</span>
        </div>
        <div className="bga-field" style={field}>
          <label className="bga-label" htmlFor="sa-read-effort">Effort — reading the card</label>
          <select id="sa-read-effort" className="bga-select" value={draft.read_effort} onChange={(e) => set('read_effort', e.target.value)}>
            {info.effort_levels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <span style={hint}>Higher reads messy handwriting more carefully — and thinks for longer, so reads take longer and cost more.</span>
        </div>
        <div className="bga-field" style={field}>
          <label className="bga-label" htmlFor="sa-read-format">Answer format — reading the card</label>
          <select id="sa-read-format" className="bga-select" value={draft.read_format} onChange={(e) => set('read_format', e.target.value)}>
            {(info.read_formats || ['full', 'compact']).map((f) => <option key={f} value={f}>{FORMAT_LABELS[f] || f}</option>)}
          </select>
          <span style={hint}>Compact asks Claude to write about a tenth as much; the app's checks are the same.</span>
        </div>
        <div className="bga-field" style={field}>
          <label className="bga-label" htmlFor="sa-read-speed">Speed — reading the card</label>
          <select id="sa-read-speed" className="bga-select" value={draft.read_speed} onChange={(e) => set('read_speed', e.target.value)}>
            {(info.read_speeds || ['standard', 'fast']).map((f) => <option key={f} value={f}>{SPEED_LABELS[f] || f}</option>)}
          </select>
          <span style={hint}>Fast mode works with Claude Opus 5 / 5.5 only; other models read at standard speed.</span>
        </div>
        <div className="bga-field" style={field}>
          <label className="bga-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={draft.read_passes >= 2} onChange={(e) => set('read_passes', e.target.checked ? 2 : 1)} /> Read each card twice
          </label>
          <span style={hint}>Two reads side by side (same wait, twice the cost). Holes they disagree on go to the reviewer instead of slipping through.</span>
        </div>
        <div className="bga-field" style={field}>
          <label className="bga-label" htmlFor="sa-chat-effort">Effort — review chat</label>
          <select id="sa-chat-effort" className="bga-select" value={draft.chat_effort} onChange={(e) => set('chat_effort', e.target.value)}>
            {info.effort_levels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="bga-field" style={field}>
          <label className="bga-label" htmlFor="sa-threshold">Unsure below (confidence)</label>
          <input id="sa-threshold" className="bga-input" type="number" step="0.05" min={0.5} max={0.99} value={draft.confidence_threshold}
            onChange={(e) => set('confidence_threshold', Number(e.target.value))} />
          <span style={hint}>Readings below this need a person — unless the card's own totals confirm them. Putts / penalties below it are left out.</span>
        </div>
      </div>

      <div style={section}>Reviewers &amp; notices</div>
      <div style={grid}>
        <div className="bga-field" style={field}>
          <label className="bga-label" htmlFor="sa-admin">Admin (profile e-mail)</label>
          <input id="sa-admin" className="bga-input" type="email" value={draft.admin_email} onChange={(e) => set('admin_email', e.target.value)} />
          <span style={hint}>Can review any scorecard, and gets the ones left waiting.</span>
        </div>
        {NUMERIC.map(({ key, label, hint: h }) => (
          <div key={key} className="bga-field" style={field}>
            <label className="bga-label" htmlFor={`sa-${key}`}>{label}</label>
            <input id={`sa-${key}`} className="bga-input" type="number" value={draft[key] as number}
              min={info.limits[key]?.min} max={info.limits[key]?.max}
              onChange={(e) => set(key, Number(e.target.value) as never)} />
            <span style={hint}>{h} {info.limits[key] ? `(${fmt(info.limits[key].min)}–${fmt(info.limits[key].max)})` : ''}</span>
          </div>
        ))}
        {([
          ['whatsapp_notify', 'WhatsApp the reviewer', 'Needs the reviewer\'s WhatsApp number on their Profile.'],
          ['refusal_fallbacks', 'Fallback model on refusal', 'If the model declines, the API retries on its recommended fallback.'],
          ['structured_output', 'Structured output', 'Guarantees the read comes back as valid JSON.'],
        ] as [keyof ScorecardAgentSettings, string, string][]).map(([key, label, h]) => (
          <div key={key} className="bga-field" style={field}>
            <label className="bga-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={!!draft[key]} onChange={(e) => set(key, e.target.checked as never)} /> {label}
            </label>
            <span style={hint}>{h}</span>
          </div>
        ))}
      </div>

      <div style={section}>House notation guide</div>
      <p className="bga-muted" style={{ fontSize: 13, margin: '0 0 8px' }}>
        How your players write their cards — sent with every read. Add a line when someone marks things their own way.
      </p>
      <textarea
        aria-label="Notation guide"
        className="bga-input"
        style={{ width: '100%', minHeight: 170, fontSize: 13, lineHeight: 1.45 }}
        value={draft.notation_guide}
        onChange={(e) => set('notation_guide', e.target.value)}
      />
      {info.suggestions.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Suggested by the review assistant</div>
          {info.suggestions.map((s) => (
            <div key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 10px', border: '1px solid var(--bga-border)', borderRadius: 8, marginBottom: 6 }}>
              <span style={{ flex: 1, fontSize: 13 }}>{s.text}</span>
              <button type="button" className="bga-btn bga-btn-primary bga-btn-sm" disabled={busy} onClick={() => resolve(s.id, true)}>Add to guide</button>
              <button type="button" className="bga-btn bga-btn-secondary bga-btn-sm" disabled={busy} onClick={() => resolve(s.id, false)}>Dismiss</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 }}>
        <button type="button" className="bga-btn bga-btn-primary" disabled={!dirty || busy} onClick={save}>Save scorecard settings</button>
        <button type="button" className="bga-btn bga-btn-secondary" disabled={busy || !info.overridden.length} onClick={reset}>Reset to defaults</button>
        {msg && <span style={{ fontSize: 13, fontWeight: 600, color: msg.ok ? '#15803d' : 'var(--bga-danger, #c0392b)' }}>{msg.text}</span>}
      </div>

      <div style={section}>Recent scorecards</div>
      {!usage || usage.uploads.length === 0 ? (
        <p className="bga-muted" style={{ fontSize: 13, margin: 0 }}>No scorecard photos yet.</p>
      ) : (
        <>
          {t && t.cells > 0 && (
            <p style={{ fontSize: 13, margin: '0 0 8px' }}>
              Read accuracy (saved cards): <b>{pct(t.kept, t.cells)}</b> of {fmt(t.cells)} holes kept exactly as read
              {t.fixed_by_total ? `; ${fmt(t.fixed_by_total)} fixed by the card's totals` : ''}; {fmt(t.edited)} changed in review.
            </p>
          )}
          <FitTable>
            <table className="tbl tbl--sm">
              <thead>
                <tr><th>Round</th><th>Status</th><th className="r">Read time</th><th className="r">Kept as read</th><th className="r">In</th><th className="r">Out</th><th className="r">Cache read</th></tr>
              </thead>
              <tbody>
                {usage.uploads.map((u) => {
                  const inTok = (u.usage.read?.input_tokens || 0) + (u.usage.chat?.input_tokens || 0);
                  const outTok = (u.usage.read?.output_tokens || 0) + (u.usage.chat?.output_tokens || 0);
                  const cache = (u.usage.read?.cache_read_input_tokens || 0) + (u.usage.chat?.cache_read_input_tokens || 0);
                  return (
                    <tr key={u.upload_id}>
                      <td>{u.course_name || 'Round'} · {u.round_date}</td>
                      <td>
                        {u.status}{u.error ? <span className="bga-muted"> — {u.error}</span> : null}
                        {u.status === 'failed' && u.read_summary && (
                          <div className="bga-muted" style={{ fontSize: '0.85em', whiteSpace: 'normal', marginTop: 2 }}>{u.read_summary}</div>
                        )}
                      </td>
                      <td className="r">
                        {mmss(u.read_ms)}
                        {u.read_timing && u.status !== 'failed' && (
                          <div className="bga-muted" style={{ fontSize: '0.8em', whiteSpace: 'nowrap' }}>
                            thinking {mmss(u.read_timing.thinking_ms)} · answer {mmss(u.read_timing.writing_ms)}
                            {u.read_timing.tokens_per_s ? ` · ${u.read_timing.tokens_per_s} tok/s` : ''}
                          </div>
                        )}
                        {u.read_setup && (
                          <div className="bga-muted" style={{ fontSize: '0.8em', whiteSpace: 'nowrap' }}>
                            {[u.read_setup.model.replace(/^claude-/, ''), u.read_setup.effort, u.read_setup.format, u.read_setup.speed === 'fast' ? 'fast' : null, (u.read_setup.passes || 1) > 1 ? `read ${u.read_setup.passes}×` : null].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="r">{u.stats ? `${pct(u.stats.kept, u.stats.cells)} (${u.stats.fixed_by_total + u.stats.edited} fixed)` : '—'}</td>
                      <td className="r">{fmt(inTok)}</td>
                      <td className="r">{fmt(outTok)}</td>
                      <td className="r">{fmt(cache)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </FitTable>
        </>
      )}
    </div>
  );
};

export default ScorecardAgentCard;
